import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import {
  countryCodePhoneInputPickerData as countries,
  countryByISOCodeMap,
} from './helper';
import { getCountryFlagImageUrl } from './country-flag-image';
import './index.scss';
import { accessibleOnClick } from '../../utils';
import { PhoneNumberCrossIcon } from '../../svg/phone-number-cross';

interface CountryCodePhoneInputPickerProps {
  containerClassName?: string;
  phoneNumber: string;
  onPhoneNumberChange: (phoneNumber: string) => void;
  defaultCountryISOCode: string; // ISO code of the default country
  onCountryChange: (country: {
    code: string;
    country: string;
    flag: string;
    iso: string;
  }) => void;
}

const CountryCodePhoneInputPicker: React.FC<CountryCodePhoneInputPickerProps> = ({
  containerClassName = '',
  phoneNumber = '',
  onPhoneNumberChange = () => {},
  defaultCountryISOCode = '',
  onCountryChange = () => {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [selectedCountry, setSelectedCountry] = useState(
    countryByISOCodeMap.get(defaultCountryISOCode?.toUpperCase() || 'US'),
  );

  // Keep selected country in sync when defaultCountryISOCode prop changes
  useEffect(() => {
    const nextCountry =
      countryByISOCodeMap.get(defaultCountryISOCode?.toUpperCase() || 'US') ??
      countryByISOCodeMap.get('US');

    if (nextCountry && nextCountry.iso !== selectedCountry?.iso) {
      setSelectedCountry(nextCountry);
    }
  }, [defaultCountryISOCode, selectedCountry]);

  const handlePhoneNumberChange = (value: string) => {
    onPhoneNumberChange(value);
  };

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter countries based on search term
  const filteredCountries = countries?.filter(
    (country) =>
      country?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      country?.code?.includes(searchTerm) ||
      country?.iso?.toLowerCase().includes(searchTerm?.toLowerCase()),
  );

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[focusedIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      searchInputRef?.current?.focus();
    }
  }, [isOpen]);

  const handleCountrySelect = useCallback(
    (country) => {
      setSelectedCountry(country);
      onCountryChange(country);
      setIsOpen(false);
      setSearchTerm('');
      setFocusedIndex(-1);
      // Focus phone input after dropdown closes (next tick so DOM is updated)
      setTimeout(() => phoneInputRef.current?.focus(), 0);
    },
    [onCountryChange],
  );

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          Math.min(prev + 1, filteredCountries.length - 1),
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && filteredCountries[focusedIndex]) {
          handleCountrySelect(filteredCountries[focusedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className={`country-code-phone-input-picker ${containerClassName}`}>
      {/* Country Code Picker */}
      <div
        className="country-code-phone-input-picker-dropdown-container"
        ref={dropdownRef}
      >
        <button
          type="button"
          className="country-code-picker-button"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span
            className="country-code-picker-button-flag"
            role="img"
            aria-label={`${selectedCountry.name} ${selectedCountry.code}`}
          >
            <img
              src={getCountryFlagImageUrl(selectedCountry.iso)}
              alt=""
              width={16}
              height={12}
              className="country-code-flag-img country-code-flag-img--button"
              loading="lazy"
              decoding="async"
            />
          </span>
          <span className="country-code-picker-button-code">
            {selectedCountry.code}
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{
              transition: 'transform 0.2s ease',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="country-code-phone-input-picker-dropdown">
            {/* Search Input */}
            <div className="country-code-phone-input-picker-dropdown-search">
              <div className="country-code-phone-input-picker-dropdown-search-input">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    opacity: 0.4,
                  }}
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                  <path
                    d="M12.5 12.5L16 16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search country or code..."
                  value={searchTerm}
                  size={4}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setFocusedIndex(-1);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  className="country-code-phone-input-picker-dropdown-search-input-field"
                />
              </div>
            </div>

            {/* Country List */}
            <div
              className="country-code-phone-input-picker-dropdown-country-list"
              ref={listRef}
            >
              {filteredCountries?.length > 0 ? (
                filteredCountries?.map((country, index) => {
                  const isSelected = country.iso === selectedCountry?.iso;
                  const isFocused = index === focusedIndex;
                  const optionClass = [
                    'country-code-phone-input-picker-dropdown-country-list-option',
                    isSelected
                      ? 'country-code-phone-input-picker-dropdown-country-list-option--selected'
                      : '',
                    isFocused
                      ? 'country-code-phone-input-picker-dropdown-country-list-option--focused'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <button
                      type="button"
                      className={optionClass}
                      key={country.iso}
                      onClick={() => handleCountrySelect(country)}
                    >
                      <span
                        className="country-code-phone-input-picker-dropdown-country-list-option-flag"
                        role="img"
                        aria-label={`${country.name} ${country.code}`}
                      >
                        <img
                          src={getCountryFlagImageUrl(country.iso)}
                          alt=""
                          width={20}
                          height={15}
                          className="country-code-flag-img country-code-flag-img--list"
                          loading="lazy"
                          decoding="async"
                        />
                      </span>
                      <span className="country-code-phone-input-picker-dropdown-country-list-option-name">
                        {country.name}
                      </span>
                      <span className="country-code-phone-input-picker-dropdown-country-list-option-code">
                        {country.code}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="country-code-phone-input-picker-dropdown-country-list-option-no-countries-found">
                  No countries found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Phone Number Input */}
      <div className="country-code-input-divider" />
      <div className="phone-input-field-container">
        <input
          ref={phoneInputRef}
          type="tel"
          placeholder="Enter phone number"
          className="phone-input-field"
          value={phoneNumber}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          size={6}
          onChange={(e) =>
            handlePhoneNumberChange(
              e?.target?.value?.replace(/[^\d#*]/g, '') ?? '',
            )
          }
          minLength={5}
          maxLength={15}
        />
        <div
          className="phone-input-field-icon"
          {...accessibleOnClick(() => {
            const newValue = phoneNumber?.slice(0, -1) ?? '';
            handlePhoneNumberChange(newValue);
            setTimeout(() => {
              phoneInputRef.current?.focus();
              const len = newValue?.length;
              phoneInputRef.current?.setSelectionRange(len, len);
            }, 0);
          })}
        >
          <PhoneNumberCrossIcon
            width={20}
            height={20}
            className="phone-input-clear-icon"
          />
        </div>
      </div>
    </div>
  );
};

export default memo(CountryCodePhoneInputPicker);
